import {STATUS_INFO} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/common/StatusChip';
import {type TraceCallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {parseRefMaybe} from '@wandb/weave/react';

import {
  aggregationFunctionOptions,
  comparisonOperatorOptions,
  REVERSE_STATUS_MAPPING,
  SPAN_ERROR_METRIC_PATHS,
  spanAggregationOptions,
  WEAVE_LATENCY_PATH,
  WEAVE_STATUS_PATH,
} from './constants';
import {
  type MetricType,
  type SourceType,
  type TimeUnit,
  type WeaveMetricThresholdConfig,
} from './types';

export const getValue = (obj: TraceCallSchema, path: string): unknown => {
  const segments = path.split('.');
  let current: unknown = obj;
  let i = 0;

  while (i < segments.length) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    const record = current as Record<string, unknown>;
    const key = segments[i];

    if (key.includes('[') && key.includes(']')) {
      const [arrayKey, indexStr] = key.split('[');
      const index = parseInt(indexStr.replace(']', ''), 10);
      const arr = record[arrayKey];
      current = Array.isArray(arr) ? arr[index] : undefined;
      i++;
    } else if (key in record) {
      current = record[key];
      i++;
    } else {
      // Key not found — try combining with subsequent segments to handle
      // keys that contain dots (e.g. model names like "gpt-3.5-turbo-0125")
      let found = false;
      for (let j = i + 1; j < segments.length; j++) {
        const combinedKey = segments.slice(i, j + 1).join('.');
        if (combinedKey in record) {
          current = record[combinedKey];
          i = j + 1;
          found = true;
          break;
        }
      }
      if (!found) {
        return undefined;
      }
    }
  }

  return current;
};

export const inferType = (
  call: TraceCallSchema,
  metricPath: string
): MetricType => {
  if (metricPath === WEAVE_STATUS_PATH) {
    return 'status';
  }

  const value = getValue(call, metricPath);

  if (typeof value === 'boolean') {
    return 'bool';
  }
  if (typeof value === 'string') {
    return 'str';
  }
  if (typeof value === 'number' || value == null) {
    return 'float';
  }
  return 'str';
};

// Spans have no sample call to infer from; the type follows the metric path.
export const spanMetricType = (metricPath: string): MetricType =>
  SPAN_ERROR_METRIC_PATHS.includes(metricPath) ? 'span_status' : 'float';

// The config UI is driven by a single metric type: spans derive it from the
// path (error rate vs numeric), calls infer it from a sample call.
export const resolveMetricType = (
  sourceType: SourceType | undefined,
  metricPath: string | undefined,
  call: TraceCallSchema | undefined
): MetricType => {
  if (sourceType === 'SPANS') {
    return spanMetricType(metricPath || '');
  }
  // Status is path-derived, so it resolves before a sample call has loaded.
  if (metricPath === WEAVE_STATUS_PATH) {
    return 'status';
  }
  if (metricPath && call) {
    return inferType(call, metricPath);
  }
  return 'float';
};

/**
 * Extracts numeric metric keys from summary.weave.costs data.
 * Costs are keyed by model name, with fields like prompt_tokens,
 * completion_tokens, prompt_tokens_total_cost, etc.
 * Only numeric fields are included (strings like provider_id are excluded).
 */
export const extractCostMetricKeys = (
  costs: Record<string, unknown>
): string[] => {
  const keys: string[] = [];
  for (const [modelName, costData] of Object.entries(costs)) {
    if (costData && typeof costData === 'object') {
      for (const [field, value] of Object.entries(
        costData as Record<string, unknown>
      )) {
        if (typeof value === 'number') {
          keys.push(`summary.weave.costs.${modelName}.${field}`);
        }
      }
    }
  }
  return keys;
};

export const getFlattenedKeysFromData = (
  data: unknown,
  prefix: string = ''
): string[] => {
  const keys: string[] = [];

  if (data === null || data === undefined) {
    return keys;
  }

  if (Array.isArray(data)) {
    keys.push(prefix || 'array');
    if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
      const subKeys = getFlattenedKeysFromData(data[0], `${prefix}[0]`);
      keys.push(...subKeys);
    }
    return keys;
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key in record) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        const currentKey = prefix ? `${prefix}.${key}` : key;
        const value = record[key];

        if (value !== null && typeof value === 'object') {
          const nestedKeys = getFlattenedKeysFromData(value, currentKey);
          keys.push(...nestedKeys);
        } else {
          keys.push(currentKey);
        }
      }
    }
  } else {
    if (prefix) {
      keys.push(prefix);
    }
  }

  return keys;
};

/**
 * Build the inputObjectVersionRefs array for querying scorer calls from a monitor.
 * For classifier monitors, synthetic scorer calls carry the monitor ref in their
 * input_refs instead of individual scorer refs, so we always include the monitor
 * ref as a fallback (the backend treats multiple refs as OR).
 */
export const buildMonitorInputRefs = (
  scorerRefs: string[],
  monitorRef?: string
): string[] => {
  return monitorRef ? [...scorerRefs, monitorRef] : [...scorerRefs];
};

/**
 * Extract the top-level field from a metric path for use as a server column.
 * The server can't handle nested paths with array indexing (e.g. "classifiers[0]"),
 * so we request just the root field and let client-side getValue handle traversal.
 */
export const getColumnRoot = (metricPath: string): string => {
  return metricPath.split('.')[0];
};

export const convertTimeToSeconds = (value: number, unit: TimeUnit): number => {
  switch (unit) {
    case 'SECONDS':
      return value;
    case 'MINUTES':
      return value * 60;
    case 'HOURS':
      return value * 3600;
    default:
      return value;
  }
};

export const convertSecondsToTimeUnit = (
  seconds: number,
  unit: TimeUnit
): number => {
  switch (unit) {
    case 'SECONDS':
      return seconds;
    case 'MINUTES':
      return seconds / 60;
    case 'HOURS':
      return seconds / 3600;
    default:
      return seconds;
  }
};

const getMonitorDisplayName = (monitorRef: string | undefined): string => {
  if (!monitorRef) {
    return '{Monitor}';
  }
  return parseRefMaybe(monitorRef)?.artifactName ?? '{Monitor}';
};

const getSourceDisplayStr = (
  metricFilter: WeaveMetricThresholdConfig['metricFilter']
): {sourceStr: string; sourceType: 'op' | 'monitor'} => {
  if (metricFilter.sourceType === 'MONITOR') {
    return {
      sourceStr: getMonitorDisplayName(metricFilter.monitorRef),
      sourceType: 'monitor',
    };
  }
  return {
    sourceStr: metricFilter.opStr || '{Op Name}',
    sourceType: 'op',
  };
};

/** Bare condition clause, e.g. reads after "...whenever " or "Alert when ". */
export const getWeaveMetricThresholdString = (
  metricFilter: WeaveMetricThresholdConfig['metricFilter']
): string => {
  if (metricFilter.sourceType === 'SPANS') {
    return getSpanThresholdString(metricFilter);
  }

  const {sourceStr, sourceType} = getSourceDisplayStr(metricFilter);
  const sourceLabel = sourceType === 'monitor' ? 'monitor' : 'op';
  const aggFn = metricFilter.aggregationFunction || 'mean';
  const aggLabel = (
    aggregationFunctionOptions.find(opt => opt.value === aggFn)?.label || aggFn
  ).toLowerCase();
  const isStatusMetric = metricFilter.metricPath === WEAVE_STATUS_PATH;

  // For status metrics, the aggregation mode (percentage/count) is expressed
  // in the status-specific description, so the window string should just
  // describe the window scope without repeating the aggregation function.
  let windowStr = '{Window}';
  if (isStatusMetric) {
    if (metricFilter.windowDuration) {
      windowStr = `the last ${metricFilter.windowDuration} seconds`;
    } else if (metricFilter.windowSize) {
      windowStr = `the last ${metricFilter.windowSize} calls`;
    }
  } else if (metricFilter.windowSize && metricFilter.windowDuration) {
    windowStr = `${aggLabel} of ${metricFilter.windowSize} calls in the last ${metricFilter.windowDuration} seconds`;
  } else if (metricFilter.windowSize) {
    windowStr = `${aggLabel} of ${metricFilter.windowSize} calls`;
  } else if (metricFilter.windowDuration) {
    windowStr = `last ${metricFilter.windowDuration} seconds (${aggLabel})`;
  }

  if (isStatusMetric) {
    const statusMatchNum = parseFloat(
      metricFilter.statusMatchValue || metricFilter.threshold || '0'
    );
    const statusType = REVERSE_STATUS_MAPPING[statusMatchNum];
    const statusLabel = statusType
      ? STATUS_INFO[statusType]?.label.toLowerCase()
      : 'unknown';

    if (aggFn === 'percentage' || aggFn === 'count') {
      const thresholdStr = metricFilter.threshold || '{Threshold}';
      const operatorStr =
        comparisonOperatorOptions
          .find(opt => opt.value === metricFilter.comparisonOperator)
          ?.label.toLowerCase() || 'greater than';
      if (aggFn === 'percentage') {
        return `${operatorStr} ${thresholdStr}% of calls from ${sourceLabel} '${sourceStr}' have status ${statusLabel} over ${windowStr}.`;
      }
      return `${operatorStr} ${thresholdStr} calls from ${sourceLabel} '${sourceStr}' have status ${statusLabel} over ${windowStr}.`;
    }

    // Legacy: direct status comparison
    const operatorStr =
      comparisonOperatorOptions
        .find(opt => opt.value === metricFilter.comparisonOperator)
        ?.label.toLowerCase() || 'equals';
    return `${sourceLabel} '${sourceStr}' has status ${operatorStr} ${statusLabel} over ${windowStr}.`;
  }

  if (metricFilter.metricPath === WEAVE_LATENCY_PATH) {
    const thresholdStr = metricFilter.threshold || '{Threshold}';
    const operatorStr =
      comparisonOperatorOptions
        .find(opt => opt.value === metricFilter.comparisonOperator)
        ?.label.toLowerCase() || 'greater than';
    return `latency from ${sourceLabel} '${sourceStr}' ${operatorStr} ${thresholdStr}ms over ${windowStr}.`;
  }

  const pathStr = metricFilter.metricPath || '{Metric Path}';
  if (metricFilter.threshold === '1' || metricFilter.threshold === '0') {
    if (metricFilter.comparisonOperator === 'EQUAL') {
      const boolStr = metricFilter.threshold === '1' ? 'true' : 'false';
      return `metric '${pathStr}' from ${sourceLabel} '${sourceStr}' equals ${boolStr} over ${windowStr}.`;
    }
  }

  const thresholdStr = metricFilter.threshold || '{Threshold}';
  const operatorStr =
    comparisonOperatorOptions
      .find(opt => opt.value === metricFilter.comparisonOperator)
      ?.label.toLowerCase() || 'greater than';

  return `metric '${pathStr}' from ${sourceLabel} '${sourceStr}' is ${operatorStr} ${thresholdStr} over ${windowStr}.`;
};

/** Human-readable summary for a spans-source (project-wide) alert. */
const getSpanThresholdString = (
  metricFilter: WeaveMetricThresholdConfig['metricFilter']
): string => {
  const aggFn = metricFilter.aggregationFunction || 'mean';
  const thresholdStr = metricFilter.threshold || '{Threshold}';
  const operatorStr =
    comparisonOperatorOptions
      .find(opt => opt.value === metricFilter.comparisonOperator)
      ?.label.toLowerCase() || 'greater than';
  const windowStr = metricFilter.windowDuration
    ? `the last ${metricFilter.windowDuration} seconds`
    : '{Window}';

  if (SPAN_ERROR_METRIC_PATHS.includes(metricFilter.metricPath || '')) {
    return aggFn === 'count'
      ? `the number of agent spans with errors is ${operatorStr} ${thresholdStr} over ${windowStr}.`
      : `${operatorStr} ${thresholdStr}% of agent spans have errors over ${windowStr}.`;
  }

  const aggLabel = (
    spanAggregationOptions.find(opt => opt.value === aggFn)?.label || aggFn
  ).toLowerCase();
  const pathStr = metricFilter.metricPath || '{Metric}';
  return `the ${aggLabel} of '${pathStr}' across agent spans is ${operatorStr} ${thresholdStr} over ${windowStr}.`;
};
